import React, { useEffect, useState } from "react";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import SearchIcon from "@mui/icons-material/Search";
import TvOutlinedIcon from "@mui/icons-material/TvOutlined";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import { LoadingState } from "../../components/UI/States";
import {
  fetchSignageAssetBlobUrl,
  formatPlaylistRange,
  playlistScheduleState,
} from "./signageApi";

// Shared building blocks for the Digital Signage dashboard (SignageDashboard
// and its Displays / Content / Branches panels).

export const ACCEPTED_FILES =
  ".jpg,.jpeg,.png,.gif,.bmp,.webp,.mp4,.mkv,.mov,.avi";

export const INPUT = "app-input w-full rounded-xl border px-3 py-2 text-sm";

// Small round icon buttons for dense rows and card headers (the 40px
// .app-icon-btn is for page/dialog headers). Pair with an aria-label.
export const ICON_BTN_SM =
  "app-muted inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full transition hover:bg-[color:var(--app-hover)] hover:text-[color:var(--app-text)] disabled:cursor-not-allowed disabled:opacity-30 disabled:hover:bg-transparent";
export const ICON_BTN_SM_DANGER =
  "app-muted inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full transition hover:bg-rose-500/10 hover:text-rose-600 dark:hover:text-rose-400";

// Segmented control (the same look the MUI theme gives ToggleButtonGroup):
// a tinted track with the active option raised.
export const SEGMENT_TRACK =
  "gap-0.5 rounded-xl bg-[color:var(--app-hover)] p-[3px]";
export const segmentClass = (active, size = "sm") =>
  `inline-flex items-center justify-center gap-1.5 whitespace-nowrap rounded-[9px] font-semibold transition ${
    size === "md" ? "px-3.5 py-1.5 text-sm" : "px-3 py-[5px] text-xs"
  } ${
    active
      ? "bg-white text-[color:var(--app-text)] shadow-sm dark:bg-white/15"
      : "app-muted hover:text-[color:var(--app-text)]"
  }`;

/**
 * Segmented control. `options`: [{ value, label, icon?, title?, badge? }];
 * `badge` may be a function of `active` (for count chips).
 */
export const Segmented = ({
  options,
  value,
  onChange,
  size = "sm",
  fullWidth = false,
  label,
  className = "",
}) => (
  <div
    role="group"
    aria-label={label}
    className={`${
      fullWidth ? "flex w-full" : "inline-flex w-fit max-w-full shrink-0 flex-wrap"
    } ${SEGMENT_TRACK} ${className}`}
  >
    {options.map((o) => {
      const active = o.value === value;
      return (
        <button
          key={o.value}
          type="button"
          aria-pressed={active}
          title={o.title}
          onClick={() => onChange(o.value)}
          className={`${segmentClass(active, size)} ${fullWidth ? "flex-1" : ""}`}
        >
          {o.icon}
          {o.label}
          {typeof o.badge === "function" ? o.badge(active) : o.badge}
        </button>
      );
    })}
  </div>
);

/** Search box with a leading magnifier. Passes the change event through. */
export const SearchInput = ({
  value,
  onChange,
  placeholder,
  className = "",
  autoFocus,
}) => (
  <div className={`relative ${className}`}>
    <SearchIcon
      className="app-muted pointer-events-none absolute left-3 top-1/2 -translate-y-1/2"
      sx={{ fontSize: 18 }}
    />
    <input
      className={`${INPUT} pl-9`}
      placeholder={placeholder}
      aria-label={placeholder}
      value={value}
      onChange={onChange}
      autoFocus={autoFocus}
    />
  </div>
);

// vcgencmd get_throttled: any nonzero bit means under-voltage or throttling
// (now or since boot) — worth surfacing, it's the classic bad-PSU symptom.
export const hasPowerIssue = (throttled) => {
  const n = parseInt(String(throttled || "0x0"), 16);
  return !isNaN(n) && n !== 0;
};

// Translucent tints so chips read on classic and glass surfaces alike.
const TONES = {
  red: "bg-red-500/15 text-red-700 dark:text-red-300",
  amber: "bg-amber-500/15 text-amber-800 dark:text-amber-300",
  sky: "bg-sky-500/15 text-sky-700 dark:text-sky-300",
  emerald: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300",
  slate: "bg-slate-500/15 text-slate-600 dark:text-slate-300",
};

export const Pill = ({ tone = "slate", title, children }) => (
  <span
    className={`inline-flex items-center gap-1 whitespace-nowrap rounded-full px-2 py-0.5 text-[11px] font-semibold ${TONES[tone]}`}
    title={title}
  >
    {children}
  </span>
);

/**
 * What's wrong with a display, most urgent first — drives the row chips,
 * the "Needs attention" filter, and the explanations in the device drawer.
 * `attention: false` marks informational items (an update installs itself).
 * playlistById is optional; without it the "nothing running today" check is
 * skipped.
 */
export const deviceIssues = (device, playlistById) => {
  const issues = [];
  if (!device.lastSeenAt) {
    issues.push({
      key: "never",
      label: "Never connected",
      tone: "slate",
      attention: true,
      detail:
        "Registered, but the Pi hasn't checked in yet. Make sure it's powered on and connected to the network.",
    });
  } else if (!device.online) {
    issues.push({
      key: "offline",
      label: "Offline",
      tone: "red",
      attention: true,
      detail:
        "The display stopped checking in. Check that the TV and the Pi behind it have power and a network connection.",
    });
  }
  if (device.mode !== "board") {
    const ids = device.playlists || [];
    if (ids.length === 0) {
      issues.push({
        key: "empty",
        label: "Nothing assigned",
        tone: "amber",
        attention: true,
        detail: "No playlist is assigned, so this TV has nothing to play.",
      });
    } else if (playlistById) {
      const assigned = ids.map((id) => playlistById.get(id)).filter(Boolean);
      if (
        assigned.length > 0 &&
        assigned.every((p) => playlistScheduleState(p) !== "running")
      ) {
        issues.push({
          key: "idle",
          label: "Nothing scheduled today",
          tone: "amber",
          attention: true,
          detail:
            "Every playlist on this display is outside its date range today. Add an everyday playlist or adjust the dates.",
        });
      }
    }
  }
  if (hasPowerIssue(device.health?.throttled)) {
    issues.push({
      key: "power",
      label: "Power warning",
      tone: "amber",
      attention: true,
      detail: `The Pi reported under-voltage or throttling (${device.health.throttled}). The power supply is usually the cause.`,
    });
  }
  if (device.command?.status === "failed") {
    issues.push({
      key: "command",
      label: `${device.command.type} failed`,
      tone: "red",
      attention: true,
      detail: device.command.output
        ? `Last remote action failed: ${device.command.output}`
        : "The last remote action failed on the display.",
    });
  }
  if (device.updateAvailable) {
    issues.push({
      key: "update",
      label: "Update pending",
      tone: "sky",
      attention: false,
      detail: `v${device.latestClientVersion} is ready on the display's server. With automatic updates on (Settings) it installs itself within the hour, or use Update software to do it now.`,
    });
  }
  return issues;
};

export const needsAttention = (device, playlistById) =>
  deviceIssues(device, playlistById).some((i) => i.attention);

// Online dot — pulses while the display is checking in.
export const StatusDot = ({ device }) => {
  const color = device.online
    ? "bg-emerald-500"
    : device.lastSeenAt
      ? "bg-red-500"
      : "bg-slate-400";
  return (
    <span className="relative flex h-2.5 w-2.5 shrink-0">
      {device.online && (
        <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-60" />
      )}
      <span className={`relative inline-flex h-2.5 w-2.5 rounded-full ${color}`} />
    </span>
  );
};

export const ErrorNote = ({ children }) =>
  children ? (
    <p
      role="alert"
      className="rounded-xl bg-rose-500/10 px-3 py-2 text-sm text-rose-700 dark:text-rose-300"
    >
      {children}
    </p>
  ) : null;

export const Loading = () => <LoadingState />;

// Empty-state panel with an optional call to action. Standalone it's its
// own card; `inset` drops the card for use inside one.
export const EmptyState = ({ icon, title, children, action, inset = false }) => (
  <div
    className={`flex flex-col items-center text-center ${
      inset ? "px-6 py-10" : "app-card rounded-2xl border px-6 py-14"
    }`}
  >
    {icon && (
      <span className="app-icon-tile mb-4 h-14 w-14 rounded-2xl bg-slate-500/10 text-slate-500 dark:text-slate-400">
        {icon}
      </span>
    )}
    {title && <p className="text-base font-semibold">{title}</p>}
    {children && (
      <p className="app-muted mt-1 max-w-md text-sm leading-relaxed">{children}</p>
    )}
    {action && <div className="mt-5">{action}</div>}
  </div>
);

// Where a playlist's date range stands today — shown next to its name
// wherever playlists are listed. Nothing for an always-on playlist.
export const ScheduleChip = ({ playlist }) => {
  const range = formatPlaylistRange(playlist);
  if (!range) return null;
  const state = playlistScheduleState(playlist);
  const tone = { running: "emerald", upcoming: "sky", ended: "slate" }[state];
  const label = { running: "running", upcoming: "scheduled", ended: "ended" };
  return (
    <Pill tone={tone} title={range}>
      {label[state]}
    </Pill>
  );
};

export const LabeledInput = ({ label, children }) => (
  <label className="flex flex-col gap-1">
    <span className="app-muted text-xs font-medium">{label}</span>
    {children}
  </label>
);

/**
 * Media preview that fetches the bytes with the auth header and shows them
 * via an object URL (a bare <img src> can't carry x-user-id). Falls back to
 * a labeled placeholder when the file can't be decoded. With `onOpen`, the
 * thumbnail becomes a button that hands its already-downloaded object URL
 * to a full-size preview (no second download of a big video).
 */
export const AssetThumb = ({
  asset,
  userId,
  sizeClass = "h-28 w-full",
  onOpen,
}) => {
  const [url, setUrl] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let revoked = null;
    let cancelled = false;
    fetchSignageAssetBlobUrl(asset.id, userId)
      .then((objectUrl) => {
        if (cancelled) {
          URL.revokeObjectURL(objectUrl);
          return;
        }
        revoked = objectUrl;
        setUrl(objectUrl);
      })
      .catch(() => !cancelled && setFailed(true));
    return () => {
      cancelled = true;
      if (revoked) URL.revokeObjectURL(revoked);
    };
    // revision: an image re-saved from the editor keeps its id.
  }, [asset.id, asset.revision, userId]);

  const box = `flex ${sizeClass} items-center justify-center rounded-lg bg-slate-500/10`;

  if (failed) {
    return (
      <div className={`${box} app-muted flex-col gap-1`}>
        <TvOutlinedIcon fontSize="small" />
        <span className="text-[10px] uppercase tracking-wide">
          {asset.type}
        </span>
      </div>
    );
  }
  if (!url)
    return <div aria-hidden="true" className={`app-skeleton flex ${sizeClass} rounded-lg`} />;
  const isVideo = asset.type === "video";
  const media = isVideo ? (
    <video
      src={url}
      className={`${sizeClass} rounded-lg bg-black object-contain`}
      muted
      preload="metadata"
      onError={() => setFailed(true)}
    />
  ) : (
    <img
      src={url}
      alt={asset.filename}
      className={`${box} object-contain`}
      onError={() => setFailed(true)}
    />
  );
  if (!onOpen) return media;

  return (
    <button
      type="button"
      onClick={() => onOpen(url)}
      title={isVideo ? "Play video" : "View full size"}
      aria-label={`${isVideo ? "Play" : "View"} ${asset.filename}`}
      className="group relative block w-full rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-500"
    >
      {media}
      <span className="pointer-events-none absolute inset-0 flex items-center justify-center rounded-lg transition group-hover:bg-black/40">
        <span
          className={`flex h-10 w-10 items-center justify-center rounded-full bg-black/60 text-white transition group-hover:scale-110 ${
            isVideo ? "" : "opacity-0 group-hover:opacity-100"
          }`}
        >
          {isVideo ? <PlayArrowIcon /> : <ZoomInIcon />}
        </span>
      </span>
    </button>
  );
};
